import { Toast } from '@byecode/ui'
import {
    getFormOfFieldCodeValidator,
    getFormOfSubFormCodeValidator,
    PageModuleForm,
    useContainerBlockContext,
    useCustomViewBlockContext
} from '@lighthouse/block'
import type { SubFormRecord } from '@lighthouse/block/src/blocks'
import { FormContainerBlock } from '@lighthouse/block/src/blocks'
import type {
    ActorEvent,
    FieldADTValue,
    FormContainerBlockAbstract,
    InputValueItem,
    RecordLikeProtocol,
    RichTextContentProtocol,
    VariableADTvalue
} from '@lighthouse/core'
import { EVENT_TYPE, VariableType } from '@lighthouse/core'
import type { FieldInputConfig, FlowLayoutNode } from '@lighthouse/shared'
import {
    fieldConvertValue,
    fieldSettingMap,
    findBlockById,
    generateText,
    getBlockValueFromBlockMap,
    getPrimaryDsSystemFieldId,
    getSingleFilter,
    getWithScopeId,
    getWithScopeIdAndRecordIdAndColumnId,
    isEmptyCellValue,
    pageStackPubSub,
    resolveFilter,
    useApplicationContext,
    useAtomAction,
    useAtomData
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'
import { useAsyncRetry } from 'react-use'
import { debounce } from 'throttle-debounce'
import { useImmer } from 'use-immer'

import { pageStackOfBlockAtom } from '@/atoms/application/state'
import { addRecordAtom, fetchFormRecordAtom, setRecordAtom, updateCellAtom } from '@/atoms/dataSource/action'
import { pageBlocksAtom } from '@/atoms/page/state'
import { useCurrentPageContext, useCurrentStackIdContext } from '@/contexts/PageContext'
import { useActionTrigger } from '@/hooks/useActionTrigger'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource, useDataSourceList } from '@/hooks/useDataSource'
import { usePageDataQueryVariableLabel } from '@/hooks/usePageDataQueryVariableLabel'
import { useParseVariableWithSubscription } from '@/hooks/useParseVariable'
import { useUserRecord } from '@/hooks/useUserRecord'
import * as srv from '@/services'

interface FormContainerControllerProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'onSubmit'> {
    blockData: FormContainerBlockAbstract
    node: FlowLayoutNode
}

const FormContainerController = (props: FormContainerControllerProps) => {
    const { blockData, node, ...rest } = props
    const { id: blockId, config } = blockData
    const { eventList, submitType, rules = [], pointer, recordId, submitButton } = config

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, pointer)
    const { schema, viewOptions, type: dsType } = dataSource || {}
    const dataSourceList = useDataSourceList(appId, envId)
    const { pageId } = useCurrentPageContext()
    const stackId = useCurrentStackIdContext()
    const { scope } = useContainerBlockContext()
    const { record: userRecord } = useUserRecord()
    const parseVariable = useParseVariableWithSubscription(recordId)
    const { run: updateCell } = useAtomAction(updateCellAtom)
    const { run: fetchFormRecord } = useAtomAction(fetchFormRecordAtom)

    const { record: viewRecord } = useCustomViewBlockContext()
    const uniqueId = useMemo(() => getWithScopeIdAndRecordIdAndColumnId(blockId, scope, viewRecord?.id), [blockId, scope, viewRecord?.id])
    const { personOptions, roleOptions, departmentOptions } = useApplicationContext()
    const parseNameVariable = useParseVariableWithSubscription(submitButton.name)
    const getPageDataQueryVariableLabel = usePageDataQueryVariableLabel()
    const parseVariable2String = useCallback(
        (v: VariableADTvalue | undefined) => {
            const field = parseNameVariable(v, {
                viewRecord
            })
            if (!field) {
                if (v?.type === VariableType.PAGE_DATA_QUERY) {
                    return getPageDataQueryVariableLabel(v)
                }
                return ''
            }

            return fieldConvertValue(field, { personOptions, roleOptions, departmentOptions })
        },
        [parseNameVariable, viewRecord, personOptions, roleOptions, departmentOptions, getPageDataQueryVariableLabel]
    )
    const handleRenderTitle = useCallback(
        (v: RichTextContentProtocol) => {
            return generateText(v, {
                variable: {
                    renderLabel: v => parseVariable2String(v.attrs.value)
                }
            })
        },
        [parseVariable2String]
    )

    const idFieldId = useMemo(
        () => (schema && viewOptions && dsType ? getPrimaryDsSystemFieldId('ID', schema, viewOptions, dsType) : 'ID'),
        [dsType, schema, viewOptions]
    )
    const [submitRecord, setSubmitRecord] = useImmer<RecordLikeProtocol | undefined>(undefined)

    const { value: initFormRecord, loading } = useAsyncRetry(() => {
        if (submitType === 'UPDATE') {
            const fieldValue = recordId ? parseVariable(recordId) : undefined
            if (!fieldValue || isEmptyCellValue(fieldValue) || !idFieldId) {
                return Promise.resolve(undefined)
            }
            return fetchFormRecord({
                filter: getSingleFilter({ fieldId: idFieldId, value: recordId ? parseVariable(recordId)?.value ?? '' : '' }),
                dsId: pointer
            })
        }
        return Promise.resolve(undefined)
    }, [pointer, recordId, idFieldId, submitType, parseVariable])

    const formRecord = useMemo(() => submitRecord || initFormRecord, [initFormRecord, submitRecord])

    const fieldBlockValueMap = useAtomData(
        pageStackOfBlockAtom,
        useCallback(s => s?.[stackId], [stackId])
    )

    const blocks = useAtomData(pageBlocksAtom(pageId))

    const formId = useMemo(() => getWithScopeId(blockId, scope), [blockId, scope])
    const getFormValues = useCallback(() => {
        const recordLayerId = viewRecord?.id
        const blockMap = fieldBlockValueMap ?? {}
        const fieldInputList: InputValueItem[] = []
        const subFormDataMap: Record<string, SubFormRecord[]> = {}

        for (const blockKey of Object.keys(blockMap)) {
            const item = getBlockValueFromBlockMap(blockMap, { blockId: blockKey, recordId: recordLayerId })
            if (!item) {
                continue
            }
            if (item.blockType === 'field' && item.formId === formId) {
                fieldInputList.push({
                    id: item.id,
                    fieldId: item.fieldId,
                    dsId: item.dsId,
                    fieldType: item.fieldType,
                    scope: item.scope,
                    source: 'form',
                    type: item.type,
                    value: item.value,
                    code: item.code
                } as InputValueItem)
            }
            if (item.blockType === 'subForm' && item.formId === formId) {
                subFormDataMap[item.id] = item.value as SubFormRecord[]
            }
        }

        return { fieldInputList, subFormDataMap }
    }, [fieldBlockValueMap, formId, viewRecord?.id])

    const { handleActionListTrigger } = useActionTrigger(blockId)
    const { run: addRecord } = useAtomAction(addRecordAtom)
    const { run: updateRecord } = useAtomAction(setRecordAtom)

    // 表单提交-start
    const handleSubmit = useCallback(
        async (params: {
            blockId: string
            pointer: string
            value: InputValueItem[]
            eventList?: ActorEvent[]
            subFormRecord?: Record<string, SubFormRecord[]>
        }) => {
            const { blockId, pointer, value, eventList, subFormRecord } = params
            // 校验字段类型与输入框类型是否匹配
            const isNotValidateType = value.some(item => {
                const inputTypes = fieldSettingMap[item.type]
                if (!item.fieldType || !inputTypes.includes(item.fieldType)) {
                    const block = findBlockById(item.id, blocks)
                    const title = block?.title
                    Toast.error(`${title}字段类型不匹配`)
                    return true
                }
                return false
            })
            if (isNotValidateType) {
                return null
            }
            const fields = value.map(({ fieldId, value, id: blockId }) => ({ fieldId, value, blockId }))
            const subFormRecords = Object.entries(subFormRecord ?? {}).reduce<SubFormRecord[]>((pre, cur) => [...pre, ...cur[1]], [])
            if (!formRecord?.id && submitType === 'UPDATE') {
                Toast.error('找不到要更新的记录')
                return null
            }
            const reqRules = rules.map(rule => {
                const { filter, checkFilter } = rule
                return {
                    ...rule,
                    filter: resolveFilter({
                        filter,
                        extraParams: {
                            parseVariable2Field: parseVariable,
                            viewRecord,
                            dataSourceList,
                            pageStackFormState: fieldBlockValueMap
                        }
                    }),
                    checkFilter: resolveFilter({
                        filter: checkFilter,
                        extraParams: {
                            parseVariable2Field: parseVariable,
                            viewRecord,
                            dataSourceList,
                            pageStackFormState: fieldBlockValueMap
                        }
                    })
                }
            })

            const res = await srv.submitForm({
                appId,
                envId,
                pageId,
                isSyncComponent: !!scope,
                blockId,
                dsId: pointer,
                fields,
                recordId: formRecord?.id,
                validator: {
                    rules: reqRules,
                    codeValidator: {
                        ...getFormOfFieldCodeValidator(value, blocks),
                        ...getFormOfSubFormCodeValidator(subFormRecords, blocks)
                    }
                },
                subFormRecord: Object.entries(subFormRecord ?? {}).map(([id, records]) => ({
                    subFormId: id,
                    subFormContent: records.map(record => ({
                        recordId: record.id,
                        columns: Object.entries(record.content).map(([id, item]) => ({ columnId: id, value: item.value }))
                    }))
                }))
            })

            const { record, failureMessage } = res

            if (failureMessage) {
                Toast.error(failureMessage)
                return res
            }

            // 有不可重复字段
            if (!record) {
                return res
            }

            if (submitType === 'UPDATE') {
                setSubmitRecord(record)
                const { dsId, id: recordId } = record
                updateRecord({
                    envId,
                    dsId,
                    recordId,
                    record
                })
            } else {
                addRecord(record)
            }
            const submitEvent = eventList?.find(ev => ev.type === EVENT_TYPE.SUBMIT)
            // 表单提交完后执行动作
            if (record && submitEvent) {
                await handleActionListTrigger(uniqueId, submitEvent, { formRecord: record })
            }

            // 更新操作也需要，因为有关联或者 filter 的场景
            pageStackPubSub.emit(`${pointer}-ADD`, record)

            return res
        },
        [
            formRecord?.id,
            submitType,
            rules,
            appId,
            envId,
            pageId,
            scope,
            blocks,
            parseVariable,
            viewRecord,
            dataSourceList,
            fieldBlockValueMap,
            setSubmitRecord,
            updateRecord,
            addRecord,
            handleActionListTrigger,
            uniqueId
        ]
    )
    // 表单提交-end

    const handleUpdateCell = useCallback(
        async (config: FieldInputConfig, recordId: string, fieldValue: FieldADTValue) => {
            const { required, fieldPointer } = config
            if (!formRecord?.id || !config || !fieldPointer) {
                return
            }
            const isUpdateWillNotPass = required && isEmptyCellValue(fieldValue)
            // 如果为空，且是必填项，进行错误提示
            if (isUpdateWillNotPass) {
                Toast.error('必填项不能为空~')
            }
            setSubmitRecord(draft => {
                if (draft) {
                    draft.content[fieldPointer] = {
                        value: fieldValue.value
                    }
                }
            })
            await updateCell(
                {
                    recordId,
                    dsId: pointer,
                    envId,
                    fieldId: fieldPointer,
                    value: fieldValue
                },
                { local: isUpdateWillNotPass }
            )
            // 创建行以后，需要通知对应订阅更新数据
            pageStackPubSub.emit(`${pointer}-ADD`)
        },
        [envId, formRecord?.id, pointer, setSubmitRecord, updateCell]
    )

    const handleDebounceUpdateCell = useMemo(() => debounce(500, handleUpdateCell), [handleUpdateCell])

    if (loading) {
        return null
    }

    return (
        <PageModuleForm
            node={node}
            block={blockData}
            blocks={blocks}
            userRecord={userRecord}
            dataSource={dataSource}
            dataSourceList={dataSourceList}
            record={formRecord}
            onUpdateCell={handleDebounceUpdateCell}
            formId={formId}
            getFormValues={getFormValues}
            onSubmit={(val, pointer, subFormRecordMap) =>
                handleSubmit({
                    blockId,
                    pointer,
                    value: val,
                    eventList,
                    subFormRecord: subFormRecordMap
                })
            }
            onRenderTitle={handleRenderTitle}
            {...rest}
        >
            <FormContainerBlock {...props} />
        </PageModuleForm>
    )
}

export default FormContainerController
